import { useEffect, useState } from 'react';
import { useEditorStore } from '../lib/editor-store.js';

/*
 * Inspector panel for document metadata (Info dict). Title/author/subject are
 * editable — the store collapses repeated patches into a single trailing
 * `edit-metadata` so the edit log doesn't bloat per keystroke. Read-only
 * provenance fields (creator/producer/dates) sit below for transparency.
 *
 * The form is uncontrolled-feeling: local state holds the editing draft, and
 * we sync to the store on blur. That way pressing Enter or Tab commits, while
 * the user can still see their typing without flicker from store updates.
 */

function fmtDate(d?: Date | string): string | undefined {
  if (!d) return undefined;
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return undefined;
  return dt.toISOString().slice(0, 10);
}

export function MetadataPanel() {
  const meta = useEditorStore((s) => s.doc?.metadata);
  const setMetadataEdit = useEditorStore((s) => s.setMetadataEdit);

  // Local draft of the editable fields. Reset whenever the underlying document
  // changes (a fresh load wipes pending edits).
  const [title, setTitle] = useState<string>(meta?.title ?? '');
  const [author, setAuthor] = useState<string>(meta?.author ?? '');
  const [subject, setSubject] = useState<string>(meta?.subject ?? '');

  useEffect(() => {
    setTitle(meta?.title ?? '');
    setAuthor(meta?.author ?? '');
    setSubject(meta?.subject ?? '');
  }, [meta?.title, meta?.author, meta?.subject]);

  if (!meta) return null;

  // Push the field to the store on blur — but only if it actually changed
  // from the document's value (or the user cleared a previously-set field).
  function commit(field: 'title' | 'author' | 'subject', next: string): void {
    const original = meta?.[field] ?? '';
    if (next === original) return;
    // edit-metadata semantics: "" clears the field (per pdf-core types comment).
    setMetadataEdit({ [field]: next });
  }

  return (
    <div className="insp__section">
      <h3>メタデータ</h3>

      <label className="insp__field">
        <span>タイトル</span>
        <input
          type="text"
          value={title}
          placeholder={meta.title ? '' : '未設定'}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => commit('title', title)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
      </label>

      <label className="insp__field">
        <span>作成者</span>
        <input
          type="text"
          value={author}
          placeholder={meta.author ? '' : '未設定'}
          onChange={(e) => setAuthor(e.target.value)}
          onBlur={() => commit('author', author)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
      </label>

      <label className="insp__field">
        <span>サブジェクト</span>
        <input
          type="text"
          value={subject}
          placeholder={meta.subject ? '' : '未設定'}
          onChange={(e) => setSubject(e.target.value)}
          onBlur={() => commit('subject', subject)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
      </label>

      {(meta.creator || meta.producer || meta.creationDate || meta.modificationDate) && (
        <div className="insp__subsection">
          {meta.creator && (
            <div className="insp__row">
              <span>Creator</span>
              <span>{meta.creator}</span>
            </div>
          )}
          {meta.producer && (
            <div className="insp__row">
              <span>Producer</span>
              <span>{meta.producer}</span>
            </div>
          )}
          {meta.creationDate && (
            <div className="insp__row">
              <span>作成日</span>
              <span>{fmtDate(meta.creationDate)}</span>
            </div>
          )}
          {meta.modificationDate && (
            <div className="insp__row">
              <span>更新日</span>
              <span>{fmtDate(meta.modificationDate)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
